import React, { useState, Fragment } from 'react';
import Button from '../../Button';
import Fieldset from '../../Fieldset';
import styles from './DDAText.scss';

// Export constants needed by AddEditDDAEntry
export const DDA_SECTION_TITLE = "Direct Debit Agreement";


const DDAText = ({ onComplete, initialPage = 0 }) => {
  const [currentPage, setCurrentPage] = useState(initialPage);

  const pages = [
    <>
      <p className={styles.paragraph}>
        Your Braintree service is provided to you by PayPal Australia Pty
        Limited ABN 93 111 195 389 (“Braintree”, “PayPal”, “us”).
      </p>

      <h3 className={styles.heading}>Merchant Details</h3>
      <p className={styles.paragraph}>
        Enter the following information at the end of the agreement
      </p>
      <ol >
        <li> Name of Account Holder</li>
        <li> Address of Account Holder</li>
        <li> Contact Phone Number</li>
        <li> Bank Name</li>
        <li> BSB Number</li>
        <li> Transactional Account Number</li>
      </ol>

      <div className={styles.divider}></div>

      <p className={styles.paragraph}>
        I declare that the Account details provided above is correct and authorise and request PayPal/Braintree (APCA
        User ID 523881 and 670319) registered to PayPal PTE LTD to arrange for funds to be debited from the Account
        at the financial institution identified above.
      </p>

      <p className={styles.paragraph}>
        By signing below, I:
      </p>
      <ol>
        <li>
          Request and authorise Braintree to direct debit the Account identified above for any payments permitted by the
          Braintree Payment Services Agreement for the Braintree Service or any other relevant agreement entered into by
          me, by using BECS;
        </li>
        <li>
          Acknowledge and agree that this DDR authorisation is governed by the terms and conditions of the Direct
          Debit Request Service Agreement below;
        </li>
        <li>
          Declare that the bank account details provided for the Account are correct; and
        </li>
        <li >
          Warrant and guarantee that I am the person that can authorise debits from the Account.
        </li>
      </ol>
    </>,

    // Page 2: Agreement
    <>
      <h3 className={styles.heading}>Direct Debit Request Service Agreement (DDA)</h3>
      <p className={styles.paragraph}>
        This Direct Debit Request Service Agreement (“DDA”) is an agreement between Braintree, a division of PayPal
        Australia Pty Limited ABN 93 111 195 389 (“Braintree”, “us” or “PayPal”) and the Account holder whose
        details appear above (‘I’, ‘me’, ‘you’, ‘your’, ‘user’) and is entered in addition to the Braintree Payment
        Services Agreement agreed between us and you. This DDA is intended to operate as a Direct Debit Request
        Service Agreement, as described in the rules and procedures for the Bulk Electronic Clearing System (BECS).
        By signing this DDA or by agreeing to this DDA in another format as provided by us to you, you agree that
        these DDA terms apply to you. This DDA is effective from the date this DDA is signed or otherwise agreed to.
        Terms not otherwise defined under this DDA are as defined in the Braintree Payment Services Agreement.
      </p>

      <p className={styles.paragraph}>
        In this section, a reference to the Account is a reference to the bank account specified by you above when you
        sign up for the Braintree Payment Service, or another bank account that you notify us can be debited under this
        DDA.
      </p>

      <p className={styles.paragraph}>
        To avoid doubt, amounts which may be debited under this DDA above include, without limitation, any fees,
        Reversals, Chargebacks, the value of chargeback transactions, authorised refunds, compliance fees, fines
        imposed by us or any other payment system participant amounts so as to process any adjustments or other losses
        and any other amounts under the Braintree Payment Services Agreement or any other agreement agreed between
        us and you.
      </p>

      <p className={styles.paragraph}>
        If any debiting of funds from the Account falls on a day that is not a banking day in the place where the Account
        is held, it will be debited from the Account on the next banking day following the debiting date.
      </p>
    </>,

    // Page 3: Terms
    <>
      <h3 className={styles.heading}>TERMS OF THE ARRANGEMENT</h3>
      <h3 className={styles.subHeading}>A. General</h3>

      <ol>
        <li>It's your responsibility to:
          <ol>
            <li className={styles.alphaList}>Check whether the Account can accept debit and credit transfers through BECS;</li>
            <li className={styles.alphaList}>Ensure that the Account has sufficient funds to allow for a debit transfer;</li>
            <li className={styles.alphaList}>Check the Account's bank statement details and verify that all amounts are correct.</li>
          </ol>
        </li>
        <li>
          If transfers are returned to Braintree by your Bank, we may charge you a fee in addition to any fees your
          Bank may charge. If a debit transfer is returned to Braintree because the Account has insufficient funds, it is
          your responsibility to fund the Account so that we can re-process the debit transfer.
        </li>
        <li>
          In the event there are insufficient funds in the Account to enable a debit to be made,
          this may result in the debit being reversed and we may attempt to withdraw these funds again within a reasonable timeframe
          after the initial debit. You also:
          <ol>
            <li className={styles.alphaList}>May be charged a fee and/or interest by your financial institution;</li>
            <li className={styles.alphaList}>May incur fees or charges imposed or incurred by us;</li>
            <li className={styles.alphaList}>Must arrange for the payment to be made by another method;</li>
            <li className={styles.alphaList}>May also have your debit deducted from a future disbursement.</li>
          </ol>
        </li>
        <li>
          If you believe there has been an error in debiting the Account or a debit has been initiated incorrectly, you
          should contact Braintree Support at <a href="https://developer.paypal.com/braintree/help">https://developer.paypal.com/braintree/help</a>.
          as soon as practicable to avoid fees and or interest charges. You can also notify your financial institution at
          which the Account is held. If there has been an error, we’ll arrange with your Bank to adjust the Account and we’ll
          notify you of the adjusted amount.
        </li>
        <li>
          If we don’t find an error, we’ll respond to your query with evidence of the correctness of the transfer.
        </li>
        <li>
          If you are not satisfied with our response for any reason, you may contact your financial institution
          who should respond to you with an answer to your claim:
          <ol>
            <li className={styles.alphaList}>Within 5 business days (for claims lodged within 12 months of the disputed debit);</li>
            <li className={styles.alphaList}>Within 30 business days (for claims lodged more than 12 months from the disputed debit).</li>
          </ol>
        </li>
        <li>
          You will receive a refund of the drawing amount if we, using our reasonable efforts, cannot substantiate the
          reason for the debit.
        </li>
      </ol>
    </>,

    // Page 4: Terms Part 2
    <>
      <h3 className={styles.heading}>TERMS OF THE ARRANGEMENT (continued)</h3>
      <h3 className={styles.subHeading}>B. Changes to this DDA and other matters</h3>
      <ol>
        <li>
          Braintree will provide you with notice of at least 30 days prior to making any changes to this DDA.
        </li>

        <li>
          If you wish to discuss any changes to the terms, please
          contact Braintree Support at <a href="https://developer.paypal.com/braintree/help">https://developer.paypal.com/braintree/help</a>.
        </li>

        <li>
          You can also request that a transfer be stopped, cancelled, or deferred
          through the relevant financial institution of the Account.
        </li>

        <li>
          You can cancel your direct debit authorisation for future transactions at any time by
          contacting Braintree Support at <a href="https://developer.paypal.com/braintree/help">https://developer.paypal.com/braintree/help</a>.
          If you cancel the DDA by notice to us, you agree that we may still debit the Account for any pending
          transactions which you have already initiated and authorised
        </li>

        <li>
          You acknowledge that revocation of the direct debit authorisation does not terminate any contract for services
          that exists between Braintree and you. The authorisation applies only to direct debits and does not otherwise
          have any bearing on the contract for services exchanged. Amounts which you owe us may be set off against
          other amounts payable by us to you under the Braintree Payment Services Agreement, or we can pursue you for
          payment in other ways as permitted by law.
        </li>
      </ol>

      <h3 className={styles.subHeading}>C. Privacy</h3>

      <p className={styles.paragraph}>
        Information relating to this DDA is treated according to
        Braintree's <a href="https://www.braintreepayments.com/en-au/legal/braintree-privacy-policy-au" >Privacy Policy</a>.
      </p>

      <p className={styles.paragraphCenter}>
        EXECUTED as an Agreement with immediate effect from the signature date (Effective Date)
      </p>
    </>
  ];

  const nextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(prevPage => prevPage + 1);
    } else {
      if (typeof onComplete === 'function') {
        onComplete();
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prevPage => prevPage - 1);
    }
  };

  return (
    <Fragment>
      <Fieldset title={`Direct Debit Agreement - Page ${currentPage + 1} of ${pages.length}`}>
        <div className={styles.container}>
          {pages[currentPage]}
          <div className={`${styles.navigationContainer} 
            ${currentPage > 0 ? styles.withPrevious : styles.withoutPrevious}`}>
            {currentPage > 0 && (
              <Button
                intent="primary"
                onClick={prevPage}
                data-testid="dda-prev-button">
                Previous
              </Button>
            )}

            <Button
              intent="primary"
              onClick={nextPage}
              data-test-id="dda-next-button">
              {currentPage === pages.length - 1 ? 'Continue' : 'Next'}
            </Button>
          </div>
        </div>
      </Fieldset>
    </Fragment>
  );
};

export { DDAText };
